// ###### IMPORTS
import React, { useState, useRef } from 'react';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/solid';
import { ArrowPathIcon, EllipsisVerticalIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useClickOutside } from 'services/useClickOutside';
import CalendarForm from './CalendarForm';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import ConfirmModal from '../../moduleComponents/ConfirmModal';
import { plannerGoogleManualSync } from '../../moduleServices/planner_google_api';
import { plannerCalendarTypeLabel, plannerCalendarTypeTag } from '../../moduleServices/planner_calendar_types';

/** Transição apenas ao fechar (abrir é instantâneo — evita piscar scrollbar). */
function CollapsibleCalendarForm({ open, className = '', children }) {
  return (
    <div
      className={[
        'overflow-hidden ease-out motion-reduce:transition-none motion-reduce:duration-0',
        open
          ? 'max-h-[2800px] transition-none duration-0'
          : 'pointer-events-none max-h-0 transition-[max-height] duration-200 motion-reduce:duration-0',
        className,
      ].join(' ')}
      aria-hidden={!open}
    >
      {children}
    </div>
  );
}

function CalendarInlineFormWrap({ children }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-slate-50/90 p-3 shadow-sm">{children}</div>
  );
}

// ###### COMPONENT
function calendarCanManualGoogleSync(cal) {
  return Boolean(cal?.sync_enabled && cal?.google_calendar_id);
}

/** ⋮ menu: Sync now + Delete — one instance per calendar row */
function CalendarRowOverflowMenu({
  calendarName,
  canSyncGoogle,
  anySyncBusy,
  syncingThisRow,
  isSubmitting,
  onSyncNow,
  onDeleteClick,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useClickOutside([rootRef], () => setOpen(false), open);

  const syncDisabled = !canSyncGoogle || anySyncBusy || isSubmitting;

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded p-1.5 text-gray-600 hover:bg-gray-200"
        disabled={isSubmitting}
        aria-label={`More actions for ${calendarName}`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full z-50 mt-1 min-w-[10.5rem] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          role="menu"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={syncDisabled}
            title={
              canSyncGoogle
                ? 'Sync this calendar with Google'
                : 'Enable Google sync and choose a calendar in Edit'
            }
            onClick={() => {
              setOpen(false);
              void onSyncNow();
            }}
          >
            <ArrowPathIcon className={`h-4 w-4 shrink-0 ${syncingThisRow ? 'animate-spin' : ''}`} />
            Sync now
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isSubmitting}
            onClick={() => {
              setOpen(false);
              onDeleteClick();
            }}
          >
            <TrashIcon className="h-4 w-4 shrink-0" />
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}

const CardCalendars = ({
  calendars = [],
  isLoading = false,
  onCreateCalendar,
  onUpdateCalendar,
  onDeleteCalendar,
  refreshCalendars,
  refreshEvents,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState(null);
  /** @type {number|string|null} */
  const [syncingCalendarId, setSyncingCalendarId] = useState(null);

  const isCreateMode = showForm && !editingCalendar;
  const editingId = editingCalendar?.id ?? null;

  const handleCreateClick = () => {
    setEditingCalendar(null);
    setShowForm(true);
  };

  const handleEditClick = (calendar) => {
    setEditingCalendar(calendar);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingCalendar(null);
  };

  const handleSave = async (calendarData) => {
    setIsSubmitting(true);
    try {
      if (editingCalendar) {
        await onUpdateCalendar(editingCalendar.id, calendarData);
      } else {
        await onCreateCalendar(calendarData);
      }
      setShowForm(false);
      setEditingCalendar(null);
    } catch (error) {
      // Error already handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (calendar) => {
    setCalendarToDelete(calendar);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!calendarToDelete) return;
    setIsSubmitting(true);
    try {
      await onDeleteCalendar(calendarToDelete.id);
      setShowDeleteModal(false);
      setCalendarToDelete(null);
    } catch (error) {
      // Error already handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setCalendarToDelete(null);
  };

  const handleGoogleSyncCalendar = async (cal) => {
    if (!calendarCanManualGoogleSync(cal) || syncingCalendarId !== null || isSubmitting) return;
    setSyncingCalendarId(cal.id);
    try {
      const data = await plannerGoogleManualSync({ calendar_id: cal.id });
      const n = Array.isArray(data?.summaries) ? data.summaries.length : 0;
      toast.success(
        n > 0
          ? `Sync finished (${n} calendar${n === 1 ? '' : 's'})`
          : 'Sync finished — no linked calendars synced',
      );
      if (typeof refreshCalendars === 'function') await refreshCalendars();
      if (typeof refreshEvents === 'function') await refreshEvents();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        'Google sync failed';
      toast.error(typeof msg === 'string' ? msg : 'Google sync failed');
    } finally {
      setSyncingCalendarId(null);
    }
  };

  const formEl = (
    <CalendarForm
      calendar={editingCalendar}
      onSave={handleSave}
      onCancel={handleCancelForm}
      isSubmitting={isSubmitting}
    />
  );

  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Calendars</h3>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <SpinnerLoad size="small" />
        </div>
      ) : calendars.length === 0 ? (
        <>
          {!isCreateMode ? (
            <div className="text-sm text-gray-500 py-4 text-center">No calendars yet. Create one to group events and to-dos!</div>
          ) : null}
          <CollapsibleCalendarForm open={isCreateMode}>
            <div>
              <p className="text-sm text-gray-500 mb-3 text-center">Create your first calendar</p>
              <CalendarInlineFormWrap>{formEl}</CalendarInlineFormWrap>
            </div>
          </CollapsibleCalendarForm>
        </>
      ) : (
        <>
          <div className="space-y-1">
            {calendars.map((cal) => (
              <React.Fragment key={cal.id}>
                <div className="flex items-center justify-between rounded-lg px-2 py-1 hover:bg-gray-50 transition-colors">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span
                      className="inline-flex shrink-0 rounded-md border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600 ring-1 ring-inset ring-slate-900/5"
                      title={plannerCalendarTypeLabel(cal.calendar_type)}
                    >
                      {plannerCalendarTypeTag(cal.calendar_type)}
                    </span>
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cal.color }}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{cal.name}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleEditClick(cal)}
                      className="rounded p-1.5 text-gray-600 hover:bg-gray-200 transition-colors"
                      disabled={isSubmitting}
                      aria-label={`Edit calendar ${cal.name}`}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <CalendarRowOverflowMenu
                      calendarName={cal.name}
                      canSyncGoogle={calendarCanManualGoogleSync(cal)}
                      anySyncBusy={syncingCalendarId !== null}
                      syncingThisRow={syncingCalendarId === cal.id}
                      isSubmitting={isSubmitting}
                      onSyncNow={() => handleGoogleSyncCalendar(cal)}
                      onDeleteClick={() => handleDeleteClick(cal)}
                    />
                  </div>
                </div>
                <CollapsibleCalendarForm open={showForm && editingId === cal.id}>
                  <div className="ml-6 border-l-2 border-blue-500 py-2 pl-3">
                    <CalendarInlineFormWrap>{formEl}</CalendarInlineFormWrap>
                  </div>
                </CollapsibleCalendarForm>
              </React.Fragment>
            ))}
          </div>

          <CollapsibleCalendarForm open={isCreateMode}>
            <div className="mt-5 border-t border-gray-200 pt-4">
              <CalendarInlineFormWrap>{formEl}</CalendarInlineFormWrap>
            </div>
          </CollapsibleCalendarForm>
        </>
      )}

      {!isLoading && !showForm ? (
        <div className="mt-5 flex justify-end border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={handleCreateClick}
            className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            aria-label="Add calendar"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
      ) : null}

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Calendar"
        message={`Are you sure you want to delete "${calendarToDelete?.name || 'this calendar'}"? Events and to-dos linked to it will be kept but will no longer be grouped.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
      />
    </div>
  );
};

export default CardCalendars;
